/**
 * How wide a right-hand side panel is: the proposal comments/changes pane, the fee-schedule category
 * editors, the existing-category and template pickers.
 *
 * It used to be 400px everywhere, which is the width it wants on the display it was chosen on and too
 * much of every smaller one. These panels are reserved out of the page's row rather than painted over
 * it, so the page gets whatever is left — and a 1280px display, which is what a 1920px monitor at 150%
 * operating-system scaling hands the browser, gave 400px of a 1008px row to the panel and 608px to the
 * proposal (#4964).
 *
 * A `vw` clamp rather than a container query because this is a claim on the window's row, made by an
 * element that sits outside the page's own pane and so has no pane to measure. A clamp rather than a
 * breakpoint step so the panel slides with the window instead of jumping partway through a drag.
 */

/** What the panel asks for where the window can spare it — the width every one of these panels was. */
export const SIDE_PANEL_MAX_WIDTH = 400;

/**
 * Below this a comment thread stops fitting a sender, a timestamp and a line of text, and the category
 * editor's two-column money fields stop fitting side by side. The panel stops narrowing here and the
 * page's row absorbs the rest; below the width where both still fit, the page stops reserving the
 * column at all — see `useProposalPageCanSeatSidePanel`.
 */
export const SIDE_PANEL_MIN_WIDTH = 320;

/**
 * The CSS length given to both the drawer's paper and the column reserved for it, so they agree.
 *
 * The slope is what makes the difference on the displays this was reported from. At 1920px the panel
 * is the 400px it always was; at 1536px — a 1920×1200 monitor at 125% scaling, which is the setup the
 * complaints came from — it is 338px, and the 62px that buys puts the page back over the width at
 * which it lays itself out comfortably rather than compactly. Below about 1450px it holds at its
 * minimum and the page keeps the rest.
 */
export const SIDE_PANEL_WIDTH = `clamp(${SIDE_PANEL_MIN_WIDTH}px, 22vw, ${SIDE_PANEL_MAX_WIDTH}px)`;

/**
 * Twice the panel, never wider than the window: for a panel that briefly holds something the
 * standard width cramps, such as the marketplace's result cards and filters in the fee schedule
 * category editor's team member picker (#5798).
 *
 * Only the drawer's paper takes it. The page keeps reserving `SIDE_PANEL_WIDTH`, so the wider paper
 * lies over the page for as long as it is wide, rather than reflowing the page underneath.
 */
export const SIDE_PANEL_WIDE_WIDTH = `min(100vw, calc(2 * ${SIDE_PANEL_WIDTH}))`;
